/**
 * Operations Settings API — V4 (PostgreSQL) only.
 * Permission management for ATEK employees + contractors.
 */

import { api } from '@/lib/api';

// Service/test accounts to exclude from permission management
const EXCLUDED_EMAILS = [
  'a.sicimli@lechiffre.ca',
  'claudecode@atek.io',
  'omar@test.io',
  'appscripts@atek.io',
  'cindyatek@yopmail.com',
];

export interface OperationsUserWithPermissions {
  /** PG UUID — used as the canonical user identifier in v4. */
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
  permission_strings: string[];
}

interface V4UsersResponse {
  users: Array<{
    pg_id: string;
    _id?: string;
    email: string;
    firstname?: string;
    lastname?: string;
    employment_type?: string | null;
    permission_strings?: string[];
  }>;
  total: number;
}

/**
 * Fetch ATEK employees + contractors with their permission_strings.
 * "Internal staff" is defined by `employment_type ∈ {employee, contractor}`,
 * not by Mongo organisation membership.
 */
export async function fetchUsersWithPermissions(
  search?: string
): Promise<OperationsUserWithPermissions[]> {
  const params: Record<string, string | number> = {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 100,
  };
  if (search) params.search = search;

  const response = await api.get<V4UsersResponse>('/v4/users', params);

  return (response.users || [])
    .filter((u) => !EXCLUDED_EMAILS.includes(u.email.toLowerCase()))
    .map((u) => ({
      _id: u.pg_id,
      email: u.email,
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      permission_strings: u.permission_strings || [],
    }));
}

/**
 * Update a user's permission_strings via the v4 PG endpoint.
 * `userId` must be the PG UUID.
 */
export async function updateUserPermissions(
  userId: string,
  permissionStrings: string[]
): Promise<void> {
  await api.put(`/v4/users/${userId}/permission_strings`, {
    permission_strings: permissionStrings,
  });
}
